import { isNone } from '@ember/utils';
import Enum from '@square/dashboard-utilities/utils/enum';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
export const Sentiment = new Enum(['NEGATIVE', 'POSITIVE', 'UNSET']);
export const Channel = new Enum(['EMAIL', 'PHONE']);
export const Fields = new Enum(['FIELDS_FIRST_NAME', 'FIELDS_LAST_NAME', 'FIELDS_PHONES', 'FIELDS_EMAILS', 'FIELDS_CARDS', 'FIELDS_GROUPS', 'FIELDS_BIRTHDAY', 'FIELDS_REFERENCE_ID', 'FIELDS_COMPANY_NAME', 'FIELDS_CONVERSATIONS', 'FIELDS_LOYALTY']);
export const CreationSource = new Enum(['APPOINTMENTS', 'COUPON_ISSUE_FEED', 'COUPON_REDEMPTION_FEED', 'MANUAL', 'POINT_OF_SALE', 'RETAIL_POS', 'GIFTCARD', 'EMAIL_COLLECTION', 'FEEDBACK', 'IMPORT', 'PAYMENTS_FEED', 'INVOICE', 'LOYALTY', 'MARKETING', 'MARKETING_FEED', 'MERGE', 'AUTOMATIC_MERGE', 'ONLINE_STORE', 'EXTERNAL_PLATFORM', 'THIRD_PARTY_IMPORT']);
export const LoyaltyBaseTier = 'BASE_TIER';
export const TimeRange = ProtoBaseModel.extend({
  startAt: attr(String),
  endAt: attr(String)
});
export const NumberRange = ProtoBaseModel.extend({
  startAt: attr(Number),
  endAt: attr(Number)
});
export const FilterValue = ProtoBaseModel.extend({
  all: repeated(String),
  none: repeated(String),
  any: repeated(String)
});
export const FilterText = ProtoBaseModel.extend({
  exact: attr(String),
  fuzzy: attr(String)
});
export const FilterCustomFieldValue = ProtoBaseModel.extend({
  boolValue: attr(Boolean),
  anyEnumValues: repeated(String),
  allEnumValues: repeated(String)
});

// Add channel types.
// https://prototype.sqprod.co/#/docs/squareup.rolodexdiscovery.service.FilterChannel#any
export const SalesChannelReferenceType = new Enum(['UNKNOWN_TYPE', 'LOCATION', 'FIRST_PARTY_INTEGRATION', 'OAUTH_APPLICATION', 'ONLINE_SITE', 'ONLINE_CHECKOUT', 'INVOICE', 'GIFT_CARD', 'GIFT_CARD_MARKETPLACE', 'RECURRING_SUBSCRIPTION']);
export const SalesChannel = ProtoBaseModel.extend({
  referenceType: attr(SalesChannelReferenceType),
  channelId: attr(String)
});
export const FilterChannel = ProtoBaseModel.extend({
  none: repeated(SalesChannel),
  any: repeated(SalesChannel)
});
export const FilterPayment = ProtoBaseModel.extend({
  date: attr(TimeRange),
  count: attr(NumberRange),
  totalSum: attr(NumberRange),
  locationTokens: attr(FilterValue),
  itemTokens: attr(FilterValue),
  itemCategoryTokens: attr(FilterValue),
  firstOccurred: attr(TimeRange),
  lastOccurred: attr(TimeRange),
  salesChannels: attr(FilterChannel)
});

// proto for bookings filters
export const FilterBooking = ProtoBaseModel.extend({
  startAt: attr(TimeRange),
  isFirst: attr(Boolean),
  isLast: attr(Boolean),
  teamMemberId: attr(FilterValue),
  serviceId: attr(FilterValue)
});
export const FilterCustomField = ProtoBaseModel.extend({
  _map: null,
  getMap() {
    return this._map;
  },
  toJSON() {
    const obj = {};
    for (const key of this._map.keys()) {
      obj[key] = this._map.get(key).toJSON();
    }
    return obj;
  }
}).reopenClass({
  create() {
    const obj = this._super();
    obj.set('_map', new Map());
    return obj;
  },
  deserialize(data) {
    if (isNone(data)) {
      return undefined;
    }
    const record = this.create({});
    for (const key of Object.keys(data)) {
      record.getMap().set(key, FilterCustomFieldValue.deserialize(data[key]));
    }
    return record;
  }
});
export const FilterConversationSentiment = ProtoBaseModel.extend({
  any: repeated(Sentiment),
  all: repeated(Sentiment),
  none: repeated(Sentiment)
});
export const FilterConversation = ProtoBaseModel.extend({
  sentiment: attr(FilterConversationSentiment),
  date: attr(TimeRange)
});
export const FilterReachable = ProtoBaseModel.extend({
  payments: attr(FilterPayment),
  channel: attr(Channel),
  permissions: attr(FilterValue)
});
export const FilterEmailType = new Enum(['PUBLIC', 'PRIVATE', 'NESTED']);
export const FilterEmail = ProtoBaseModel.extend({
  type: attr(FilterEmailType),
  value: attr(FilterText)
});
export const FilterPhoneType = new Enum(['PUBLIC', 'PRIVATE', 'NESTED']);
export const FilterPhone = ProtoBaseModel.extend({
  type: attr(FilterPhoneType),
  value: attr(FilterText)
});
export const FilterCardType = new Enum(['PUBLIC', 'PRIVATE']);
export const FilterCard = ProtoBaseModel.extend({
  token: attr(String),
  type: attr(FilterCardType)
});
export const FilterLoyalty = ProtoBaseModel.extend({
  phone: attr(String),
  pointBalance: attr(NumberRange),
  enrolledAt: attr(TimeRange),
  tierBenefitPackageId: attr(FilterValue)
});
export const Filters = ProtoBaseModel.extend({
  createdAt: attr(TimeRange),
  updatedAt: attr(TimeRange),
  creationSource: attr(FilterValue),
  groups: attr(FilterValue),
  payments: repeated(FilterPayment),
  customFields: attr(FilterCustomField),
  conversations: attr(FilterConversation),
  has: repeated(Fields),
  hasNot: repeated(Fields),
  reachable: attr(FilterReachable),
  emails: repeated(FilterEmail),
  phones: repeated(FilterPhone),
  cards: repeated(FilterCard),
  loyalty: attr(FilterLoyalty),
  birthday: attr(TimeRange),
  referenceId: attr(FilterText),
  booking: attr(FilterBooking),
  customerType: attr(FilterValue),
  // util function for easier field manipulation
  setHasField(field) {
    const hasFields = this.has || [];
    if (hasFields.includes(field)) {
      return;
    }
    this.set('has', [...hasFields, field]);
  },
  setHasNotField(field) {
    const hasNotFields = this.hasNot || [];
    if (hasNotFields.includes(field)) {
      return;
    }
    this.set('hasNot', [...hasNotFields, field]);
  },
  addPayment(filterPayment) {
    const payments = this.payments || [];
    this.set('payments', [...payments, filterPayment]);
  },
  addCustomField(key, customField) {
    if (!this.customFields) {
      this.set('customFields', FilterCustomField.create());
    }
    this.customFields.getMap().set(key, customField);
  },
  addBooking(filterBooking) {
    this.set('booking', filterBooking);
  },
  addLoyalty(value, hasLoyaltyTiers, numberOfLoyaltyTiers) {
    if (hasLoyaltyTiers && value?.condition) {
      if (numberOfLoyaltyTiers === value?.value?.length) {
        // all tiers are selected
        this.setHasField(Fields.FIELDS_LOYALTY);
      } else {
        this.set('loyalty', FilterLoyalty.create({
          tierBenefitPackageId: FilterValue.create({
            any: value?.value
          })
        }));
      }
    } else if (!hasLoyaltyTiers && value) {
      this.setHasField(Fields.FIELDS_LOYALTY);
    } else {
      this.setHasNotField(Fields.FIELDS_LOYALTY);
    }
  },
  setCustomerTypeAlias() {
    const filterValue = FilterValue.create({
      any: ['ALIAS']
    });
    this.set('customerType', filterValue);
  }
});

// request / response
export const PaginationRequest = ProtoBaseModel.extend({
  size: attr(Number),
  cursor: attr(String)
});
export const PaginationResponse = ProtoBaseModel.extend({
  size: attr(Number),
  cursor: attr(String),
  total: attr(Number)
});
export const SortOrder = new Enum(['SORT_ORDER_ASC', 'SORT_ORDER_DESC']);

// https://prototype.sqprod.co/#/docs/squareup.rolodexdiscovery.service.SortField
export const SortField = new Enum(['SORT_FIELD_DEFAULT', 'SORT_FIELD_CREATED_AT', 'SORT_FIELD_PAYMENTS_LAST_DATE', 'SORT_FIELD_PAYMENTS_COUNT', 'SORT_FIELD_PAYMENTS_AVERAGE', 'SORT_FIELD_PAYMENTS_TOTAL', 'SORT_FIELD_PAYMENTS_FIRST_DATE', 'SORT_FIELD_CUSTOMER_TOKEN', 'SORT_FIELD_RELEVANCE_SCORE']);
export const Sort = ProtoBaseModel.extend({
  field: attr(SortField),
  order: attr(SortOrder)
});
export const FilterConditions = ProtoBaseModel.extend({
  all: repeated(Filters),
  any: repeated(Filters),
  none: repeated(Filters)
});